import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  DialogContentText,
  AlertColor
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { ConnectorInfo, keyValueApi } from 'types/types';
import { getRequest, postRequest } from 'utils/helper';
import { ToastSnackBar } from '../Setting/ToastSnackBar';

const useStyles = makeStyles((theme) => ({
  header: {
    paddingLeft: theme.spacing(4)
  },
  box: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '40px'
  },
  textField: {
    marginBottom: theme.spacing(3.75)
  }
}));

type EmailNoticationDialogProps = {
  info: ConnectorInfo;
  open: boolean;
  handleEmailDialogClose: () => void;
};

export const EmailNotificationDialog: React.FC<EmailNoticationDialogProps> = ({
  info,
  open,
  handleEmailDialogClose: handleEmailDialogClose
}: EmailNoticationDialogProps) => {
  const [textValue, setTextValue] = useState('');
  const [initialData, setInitialData] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);

  const classes = useStyles();
  const { formatMessage } = useIntl();

  const checkInValidEmailId = useCallback((value: string) => {
    const emails = value.split(',').map((email: string) => email.trim());

    if (emails.length === 0) return 0;

    const isValidEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.some((email: string) => !isValidEmailRegex.test(email));
  }, []);

  const handleChange = useCallback(
    (event: any) => {
      const value = event.target.value;

      setTextValue(value);
      const inValid = checkInValidEmailId(value);

      info.installed && setIsDisabled(inValid || initialData === value);
    },
    [checkInValidEmailId, info.installed, setIsDisabled, initialData]
  );

  const onSnackbarClose = useCallback(() => {
    setIsSnackBarOpen(false);
  }, []);
  const onSnackbarOpen = useCallback(() => {
    setIsSnackBarOpen(true);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const queryParams = {
        connectorName: info.shortName,
        companyKey: info.companyKey,
        key: keyValueApi.email
      };
      try {
        const res = await getRequest(
          '/integration-settings-api/dag-configuration',
          queryParams,
          true
        );
        if (res.data) {
          setTextValue(res.data);
          setInitialData(res.data);
        }
      } catch (error: any) {
        if (error.response) {
          console.log({ error: error.response });
        }
      }
    };
    fetchData();
  }, [info.companyKey, info.shortName, info, setTextValue, setInitialData]);

  const handleApiCall = useCallback(async () => {
    try {
      const body = {
        connectorName: info.shortName,
        companyKey: info.companyKey,
        email: textValue,
        key: keyValueApi.email
      };
      const res = await postRequest(
        '/integration-settings-api/dag-configuration',
        body,
        false
      );
      if (res.data) {
        setSeverity('success');
        setInitialData(res.data);
        onSnackbarOpen();
        setIsDisabled(true);
      }
    } catch (error: any) {
      setSeverity('error');
      onSnackbarOpen();
    }
  }, [
    info.companyKey,
    info.shortName,
    textValue,
    onSnackbarOpen,
    setIsDisabled,
    setInitialData
  ]);

  const handleSave = async (event: any) => {
    event.preventDefault();
    await handleApiCall();
  };
  const alertMessage = useMemo(() => {
    if (severity === 'error') {
      return formatMessage({
        id: 'needHelpSection.notificationEmailSection.errorSnackbarMessage'
      });
    } else if (severity === 'success') {
      return formatMessage({
        id: 'needHelpSection.notificationEmailSection.successSnackbarMessage'
      });
    }
  }, [formatMessage, severity]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleEmailDialogClose}
        data-testid="checkAllDialogContent"
      >
        <DialogTitle data-testid="settingsTitle">
          <FormattedMessage
            id={'needHelpSection.notificationEmailSection.settingsTitle'}
          />
        </DialogTitle>
        <DialogContent>
          <DialogContentText data-testid="checkDialogContentText">
            <FormattedMessage
              id={'needHelpSection.notificationEmailSection.contextText'}
            />
          </DialogContentText>

          <Box className={classes.box} data-testid="box-render">
            <TextField
              id="notificationEmail"
              variant="outlined"
              fullWidth
              value={textValue}
              onChange={handleChange}
              type={'text'}
              label={
                <FormattedMessage
                  id={
                    'needHelpSection.notificationEmailSection.notificationEmail'
                  }
                />
              }
              helperText={
                <FormattedMessage
                  id={'needHelpSection.notificationEmailSection.helperText'}
                />
              }
              className={classes.textField}
              InputLabelProps={{
                shrink: true
              }}
              inputProps={{
                'data-testid': 'inputFieldSection'
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleEmailDialogClose}
            color="grey"
            autoFocus
            variant="contained"
            data-testid="cancelButton"
          >
            <FormattedMessage
              id={'needHelpSection.notificationEmailSection.cancelButton'}
            />
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isDisabled}
            data-testid="saveButton"
          >
            <FormattedMessage
              id={'needHelpSection.notificationEmailSection.saveButton'}
            />
          </Button>
        </DialogActions>
        <ToastSnackBar
          show={isSnackBarOpen}
          category={severity}
          message={alertMessage}
          onClose={onSnackbarClose}
        />
      </Dialog>
    </>
  );
};
